import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { num, dateIt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Undo2 } from "lucide-react";
import { ResoDialog } from "./ResoDialog";

// Tab Resi: elenco dei resi a magazzino (ricavati dai prelievi) + pulsante Nuovo reso
export function ResiTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: prelievi = [] } = useQuery({ queryKey: ["mag-prelievi"], queryFn: async () => (await api.get("/magazzino/prelievi")).data });
  const resi = prelievi.flatMap((p) => (p.resi || []).map((rs) => ({ ...rs, prelievo: p }))).sort((a, b) => (b.data || "").localeCompare(a.data || ""));
  const invalid = () => ["mag-prelievi", "mag-giacenze", "mag-commessa", "mag-giacenza-articolo"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="p-4 flex items-center justify-between gap-3 flex-wrap border-b border-slate-100">
        <div className="text-sm text-slate-500" data-testid="resi-conteggio">{resi.length} resi · merce rientrata in giacenza da prelievi su commesse, prestazioni o uso interno</div>
        <Button onClick={() => setOpen(true)} data-testid="reso-nuovo"><Undo2 className="h-4 w-4 mr-1.5" /> Nuovo reso</Button>
      </div>
      <div className="divide-y divide-slate-100">
        {resi.map((rs) => (
          <div key={rs.id} className="p-4" data-testid={`reso-${rs.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-sm">{dateIt(rs.data)}</span>
              <span className="font-semibold text-slate-800">{rs.prelievo.dipendente_nome}</span>
              {rs.prelievo.destinazione === "interno" ? <Badge variant="outline">Uso interno</Badge> : <Badge className="bg-orange-100 text-primary hover:bg-orange-100">{rs.prelievo.commessa_codice}</Badge>}
              <span className="text-slate-500 text-sm">da prelievo del {dateIt(rs.prelievo.data)}</span>
              {rs.registrato_da && <span className="ml-auto text-xs text-slate-400">registrato da {rs.registrato_da}</span>}
            </div>
            <div className="mt-2 text-xs text-slate-600 grid sm:grid-cols-2 gap-x-6 gap-y-1">
              {rs.righe.map((x, i) => { const r = rs.prelievo.righe.find((y) => y.articolo_id === x.articolo_id && (y.variante || "") === (x.variante || "")) || {}; return (
                <div key={i} className="flex justify-between gap-2"><span><span className="font-mono">{x.codice}</span> {r.descrizione || ""}{x.variante ? ` · ${x.variante}` : ""}</span><span className="font-mono text-emerald-700">+{num(x.quantita, 2)} {r.um || ""}</span></div>
              ); })}
            </div>
            {rs.note && <div className="mt-1 text-xs text-slate-400 italic">{rs.note}</div>}
          </div>
        ))}
        {resi.length === 0 && <div className="p-6 text-center text-slate-400 text-sm" data-testid="resi-vuoto">Nessun reso registrato.</div>}
      </div>
      {open && <ResoDialog open onClose={() => setOpen(false)} prelievi={prelievi} onSaved={invalid} />}
    </div>
  );
}
